import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '../data/DataProvider.js';
import { isOnline } from '../platform/capabilities.js';
import { IconoAlerta, IconoSinConexion } from './iconos.js';
import './aviso-sync.css';

/**
 * El estado de la sincronización, EN MÓVIL (§7 de UI_UX_SPEC.md, requisito §72).
 *
 * POR QUÉ EXISTE
 *
 * `SyncIndicator` vive en el pie de la barra lateral, y la barra lateral es
 * `display: none` por debajo de 1024 px. O sea que en el teléfono —el
 * dispositivo con el que se entrena, y el único que pierde cobertura en un
 * sótano— no se veía NUNCA ni «Sin conexión», ni «Sincronizando», ni un
 * conflicto. Ningún test lo notaba: jsdom no aplica CSS, así que el indicador
 * estaba en el DOM y «se veía» en todos.
 *
 * LO QUE ENSEÑA, SEGÚN LA MATRIZ DEL §7
 *
 * - Sin conexión: una banda arriba que dice que no se pierde nada.
 * - Conflicto o error: una banda con la acción, que lleva a Más →
 *   Sincronización. Ahí está el detalle y no hay resolución manual (CA-9).
 * - Sincronizando o pendiente: una línea discreta, no una banda. Se arregla
 *   sola en la siguiente vuelta y no merece interrumpir.
 * - Sincronizado: nada. La aplicación actúa en silencio (SYNC_ENGINE §7).
 * - En una sesión activa, nada de nada: la sesión funciona entera sin red, y
 *   una banda encima de «Guardar serie» solo estorba.
 *
 * `offline` CON RED NO ES «SIN CONEXIÓN»
 *
 * `DataProvider` arranca con `status = 'offline'` hasta la primera vuelta. Sin
 * esta comprobación, cada apertura de la aplicación enseñaba «Sin conexión»
 * durante lo que tarda en abrirse la base local, con la red perfecta.
 */
export function AvisoDeSincronizacion({ concentrado }: { concentrado: boolean }): ReactNode {
  const { status, pending, conflicts } = useAppData();
  if (concentrado) return null;

  if (status === 'offline') {
    if (isOnline()) return null;
    return (
      <div className="aviso-sync aviso-sync--sin-conexion" role="status">
        <IconoSinConexion tam={18} />
        <p>Sin conexión. Lo que hagas se guarda en este dispositivo y sube al volver.</p>
      </div>
    );
  }

  if (status === 'conflict' || status === 'error') {
    const cuantos = conflicts.length;
    const texto =
      status === 'error'
        ? 'No se ha podido sincronizar. Tus datos siguen guardados aquí.'
        : cuantos > 1
          ? `${String(cuantos)} cambios chocan con los de otro dispositivo.`
          : 'Un cambio choca con el de otro dispositivo.';
    return (
      <div className="aviso-sync aviso-sync--problema" role="status">
        <IconoAlerta tam={18} />
        <p>{texto}</p>
        <Link to="/mas/sincronizacion" className="aviso-sync__enlace">
          {status === 'error' ? 'Ver por qué' : 'Revisar'}
        </Link>
      </div>
    );
  }

  if (status === 'syncing' || (status === 'pending' && pending > 0)) {
    return (
      <p className="aviso-sync aviso-sync--discreto" role="status">
        <span className="aviso-sync__punto" aria-hidden="true" />
        {status === 'syncing'
          ? 'Sincronizando…'
          : `${String(pending)} ${pending === 1 ? 'cambio pendiente' : 'cambios pendientes'}`}
      </p>
    );
  }

  return null;
}
